import React, { useEffect, useState, useCallback } from 'react';
import { gql, useQuery } from '@apollo/client';
import { DropDown, ToggleBlock } from 'anf-core-react';

import SizeTables from './SizeTables';
import ESpotContent from '../ESpotContent/ESpotContent';

const query = gql`
  query Product($chartName: String!, $sizeChartSeq: String!, $kicId: String!) {
    sizeGuide(chartName: $chartName, sizeChartSeq: $sizeChartSeq, kicId: $kicId) {
      guides {
        description
        name
        title
        tables {
          size
          values {
            country
            unit
            value
          }
        }
      },
      availableRegions
      defaultRegion
    }
  }
`;

const MEASUREMENT_UNITS = {
  USA: { unit: 'in', weight: 'lb' },
  OTHER: { unit: 'cm', weight: 'kg' },
};

export default function SizePanel({
  sizeChartName, sizeChartSeq, tmntData, eSpotContentData, kicId,
}) {
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedMeasurement, setSelectedMeasurement] = useState(MEASUREMENT_UNITS.USA);

  const { data, previousData } = useQuery(query, {
    variables: { chartName: sizeChartName, sizeChartSeq, kicId },
  });

  const sizeGuide = (data || previousData)?.sizeGuide;
  const MEASUREMENTS = [
    { label: tmntData?.sizeGuideInch?.value, value: MEASUREMENT_UNITS.USA },
    { label: tmntData?.sizeGuideCm?.value, value: MEASUREMENT_UNITS.OTHER },
  ];
  const REGIONS = sizeGuide?.availableRegions?.map((region) => (
    { label: region, value: region }
  )) || [];

  const updateMeasurement = useCallback((region) => {
    const measurement = region === 'USA' ? MEASUREMENT_UNITS.USA : MEASUREMENT_UNITS.OTHER;
    setSelectedMeasurement(measurement);
  }, []);

  const handleRegionChange = ({ target }) => {
    setSelectedRegion(target.value);
    updateMeasurement(target.value);
  };

  useEffect(() => {
    setSelectedRegion(sizeGuide?.defaultRegion);
    updateMeasurement(sizeGuide?.defaultRegion);
  }, [sizeGuide?.defaultRegion, updateMeasurement]);

  return (
    <div>
      <ESpotContent eSpotData={eSpotContentData?.pdpESpots.pdp_sizefitguide} />
      <div className="region-and-measurement">
        {REGIONS.length > 0 && (
          <DropDown
            id="sizepanel-region"
            label={tmntData?.sizeGuideRegionSelector?.value}
            defaultValue={sizeGuide?.defaultRegion}
            onChange={handleRegionChange}
            options={REGIONS}
            value={selectedRegion}
          />
        )}
        <div aria-label="measurement" className="toggle measurement-toggle" role="radiogroup">
          {MEASUREMENTS.map(({ label, value }) => (
            <ToggleBlock
              key={`measurement-${label}`}
              id={`measurement-${label}`}
              name="sizepanel-measurement"
              value={value.unit}
              isChecked={selectedMeasurement.unit === value.unit}
              onChange={() => setSelectedMeasurement(value)}
            >
              {label}
            </ToggleBlock>
          ))}
        </div>
      </div>
      <div className="size-tables__container">
        {sizeGuide?.guides && sizeGuide?.guides.map((table) => (
          <SizeTables
            data-testid="size-table"
            key={table.title}
            table={table}
            measurement={selectedMeasurement}
            region={selectedRegion}
          />
        ))}
      </div>
    </div>
  );
}
